import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { Modal } from '@carbon/react';
import routes from '~/routes';
import { SuccessToast } from 'components/success-toast';
import ErrorToast from 'components/error-toast';
import {
  contractsAtom,
  curvesAtom,
  useRefreshContracts,
  useRefreshCurves,
} from 'pages/pricing/shared/atoms';
import { pricingApi } from 'pages/pricing/shared/api';
import type {
  Contract,
  Curve,
  CurveOption,
  Leg,
} from 'pages/pricing/shared/types';
import { CurveLibrary } from './curve-library';
import { CurveDetail, type CurveFormData } from './curve-detail';

type Toast = { type: 'success' | 'error'; message: string; subtitle?: string };

const blankForm = (): CurveFormData => ({
  name: '',
  market: 'crude',
  units: '$/bbl',
  currency: 'USD',
  frequency: 'monthly',
  category: 'benchmark',
  tradingCalendar: 'NYMEX',
  status: 'draft',
  resolution: '0.01',
  exchange: '',
  symbol: '',
  priceFallback: '',
  volatilityFallback: '',
  owner: '',
  source: '',
  notes: '',
  derivedPrice: false,
  derivedVolatility: false,
  priceLegs: [],
  volLegs: [],
});

const toForm = (
  curve: Curve,
  priceLegs: Leg[],
  volLegs: Leg[]
): CurveFormData => ({
  name: curve.name ?? '',
  market: curve.market ?? '',
  units: curve.units ?? '',
  currency: curve.currency ?? '',
  frequency: curve.frequency ?? '',
  category: curve.category ?? '',
  tradingCalendar: curve.tradingCalendar ?? '',
  status: curve.status ?? '',
  resolution: curve.resolution ?? '0.01',
  exchange: curve.exchange ?? '',
  symbol: curve.symbol ?? '',
  priceFallback:
    curve.priceFallback !== null ? String(curve.priceFallback) : '',
  volatilityFallback:
    curve.volatilityFallback !== null ? String(curve.volatilityFallback) : '',
  owner: curve.owner ?? '',
  source: curve.source ?? '',
  notes: curve.notes ?? '',
  derivedPrice: priceLegs.length > 0,
  derivedVolatility: volLegs.length > 0,
  priceLegs,
  volLegs,
});

export function CurveManagementContent() {
  const curves = useAtomValue(curvesAtom);
  const contracts = useAtomValue(contractsAtom);
  const refreshCurves = useRefreshCurves();
  const refreshContracts = useRefreshContracts();

  // Selection lives in the URL: /pricing/curve-management (list), .../new (create/clone
  // draft), .../:id (existing curve). This makes curves deep-linkable and makes the browser
  // Back button pop detail → list. The create/clone draft is in-memory (an unsaved clone has
  // no address), so a hard reload of .../new yields a fresh blank form — expected.
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const isNew = id === 'new';
  const activeId = id && id !== 'new' ? Number(id) : null; // viewing an existing curve
  const [draft, setDraft] = useState<CurveFormData | null>(null); // create/clone prefill
  const [loaded, setLoaded] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const refresh = useCallback(
    () => Promise.all([refreshCurves(), refreshContracts()]),
    [refreshCurves, refreshContracts]
  );

  useEffect(() => {
    refresh()
      .catch((e: Error) => setToast({ type: 'error', message: e.message }))
      .finally(() => setLoaded(true));
  }, [refresh]);

  // Keep the in-memory draft in sync with the URL so Back/Forward and reloads behave:
  // landing on .../new with no draft (direct link / reload) seeds a blank form; leaving
  // .../new (Back to list or into an existing curve) clears any stale draft.
  useEffect(() => {
    if (isNew && draft === null) setDraft(blankForm());
    if (!isNew && draft !== null) setDraft(null);
  }, [isNew, draft]);

  const curveOptions: CurveOption[] = useMemo(
    () => curves.map((c) => ({ value: c.id, label: c.name })),
    [curves]
  );

  // Exchange is free text with a typeahead over the venues already in use. Suggestions come from
  // every saved curve — self included, so the curve's own venue stays on the list while editing.
  const exchangeOptions: string[] = useMemo(
    () =>
      Array.from(
        new Set(curves.map((c) => (c.exchange ?? '').trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [curves]
  );

  const legsFor = useCallback(
    (curveId: number, type: Contract['compositionType']) =>
      contracts.find((k) => k.curveId === curveId && k.compositionType === type)
        ?.legs ?? [],
    [contracts]
  );

  const activeCurve = curves.find((c) => c.id === activeId) ?? null;
  const initial: CurveFormData | null = draft
    ? draft
    : activeCurve
      ? toForm(
          activeCurve,
          legsFor(activeCurve.id, 'price'),
          legsFor(activeCurve.id, 'volatility')
        )
      : null;
  const selfId = activeCurve?.id ?? null;

  const openCurve = useCallback(
    (cid: number) => {
      setDraft(null);
      history.push(routes.pricingCurveDetailPath.replace(':id', String(cid)));
    },
    [history]
  );

  const backToList = useCallback(() => {
    setDraft(null);
    history.push(routes.pricingCurveManagementPath);
  }, [history]);

  const newCurve = useCallback(() => {
    setDraft(blankForm());
    history.push(routes.pricingCurveDetailPath.replace(':id', 'new'));
  }, [history]);

  const cloneCurve = useCallback(() => {
    if (!activeCurve) return;
    setDraft({
      ...toForm(
        activeCurve,
        legsFor(activeCurve.id, 'price').map((l) => ({ ...l })),
        legsFor(activeCurve.id, 'volatility').map((l) => ({ ...l }))
      ),
      name: activeCurve.name ? `${activeCurve.name} (copy)` : '',
      status: 'draft',
    });
    history.push(routes.pricingCurveDetailPath.replace(':id', 'new'));
  }, [activeCurve, legsFor, history]);

  // Deep-link / stale-link guard: a numeric id in the URL that resolves to no curve
  // (never existed, or was just deleted) bounces back to the list once data has loaded.
  useEffect(() => {
    if (loaded && activeId !== null && !activeCurve) backToList();
  }, [loaded, activeId, activeCurve, backToList]);

  // Persist (create or update); the curve + its canonical compositions save in one transaction.
  const handleSave = async (
    payload: Partial<Curve>,
    creating: boolean
  ): Promise<Curve> => {
    try {
      const saved = creating
        ? await pricingApi.createCurve(payload)
        : await pricingApi.updateCurve(activeId as number, payload);
      await refresh();
      if (creating) {
        setDraft(null);
        // land on the new curve's own URL (view mode) — replace so Back skips the draft
        history.replace(
          routes.pricingCurveDetailPath.replace(':id', String(saved.id))
        );
      }
      setToast({
        type: 'success',
        message: creating ? 'Curve created' : 'Changes saved',
        subtitle: saved.name,
      });
      return saved;
    } catch (e) {
      setToast({ type: 'error', message: (e as Error).message });
      throw e;
    }
  };

  const confirmDelete = async () => {
    if (deleteId === null) return;
    const removedName = curves.find((c) => c.id === deleteId)?.name ?? '';
    try {
      await pricingApi.deleteCurve(deleteId);
      await refresh();
      if (activeId === deleteId) backToList();
      setToast({
        type: 'success',
        message: 'Curve deleted',
        subtitle: removedName,
      });
    } catch (e) {
      setToast({ type: 'error', message: (e as Error).message });
    } finally {
      setDeleteId(null);
    }
  };

  const pendingDelete = curves.find((c) => c.id === deleteId) ?? null;

  return (
    <>
      {!isNew && activeId === null && (
        <CurveLibrary curves={curves} onOpen={openCurve} onNew={newCurve} />
      )}

      {(isNew || activeId !== null) && initial && (
        <CurveDetail
          key={isNew ? 'new' : (activeId ?? 'detail')}
          initial={initial}
          isNew={isNew}
          curveId={activeId ?? undefined}
          updatedAt={activeCurve?.updatedAt}
          curveOptions={curveOptions.filter((o) => o.value !== selfId)}
          otherCurves={curves.filter((c) => c.id !== selfId)}
          exchangeOptions={exchangeOptions}
          onSave={handleSave}
          onBack={backToList}
          onRequestDelete={() => activeId !== null && setDeleteId(activeId)}
          onRequestClone={cloneCurve}
        />
      )}

      <Modal
        open={Boolean(pendingDelete)}
        danger
        modalHeading={pendingDelete ? `Delete ${pendingDelete.name}?` : ''}
        modalLabel="Curve Management"
        primaryButtonText="Delete curve"
        secondaryButtonText="Cancel"
        onRequestClose={() => setDeleteId(null)}
        onRequestSubmit={confirmDelete}
      >
        This permanently removes the curve and any compositions that define it.
        Curves referenced by other curves, contracts, or transformations cannot
        be deleted.
      </Modal>

      {toast?.type === 'success' && (
        <SuccessToast
          message={toast.message}
          subtitle={toast.subtitle}
          onClose={() => {
            setToast(null);
            return true;
          }}
        />
      )}
      {toast?.type === 'error' && (
        <ErrorToast
          message={toast.message}
          subtitle={toast.subtitle}
          onClose={() => {
            setToast(null);
            return true;
          }}
        />
      )}
    </>
  );
}
